import { UTCDate } from '@date-fns/utc';
import { subMonths } from 'date-fns';

import {
  isRelativeDateRangePreset,
  RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK,
} from '@revelio/core/global-ai-assistant/date-range-presets';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { dateRangeDefinition } from '../../definitions/dateRange';
import type { DateRangeFilter } from '../../types/filters';

const parseAiDateToUtcDate = (
  dateStr: string | null | undefined
): UTCDate | null => {
  if (!dateStr) {
    return null;
  }
  // dash-meta now emits full ISO dates (YYYY-MM-DD). Keep the day; fall back to
  // the first of the month if only YYYY-MM is present. Consumers decide
  // granularity (Talent Discovery floors to the month, Posting uses the day).
  const match = dateStr.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = match[3] ? Number(match[3]) : 1;
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  return new UTCDate(year, month - 1, day);
};

/**
 * Maps top-level AI Search `date_range` (not in filters.expr) to a CustomDash DATE_RANGE filter.
 *
 * When the window came from a relative-window PRESET ('last-year', …), the
 * server resolved it against the GLOBAL data max — but a trial account with a
 * data lag sees a lagged max, so the server window can sit entirely past the
 * end of the account's data. Passing `systemMaxDate` (the account's
 * lag-aware latest date, from useAvailableDateRangeWithTrialLag) re-resolves
 * the preset with the SAME subMonths arithmetic the manual date-filter preset
 * uses, anchored where this account's data actually ends. Without a
 * `systemMaxDate` (index still loading) the server-resolved dates apply.
 */
export const transformAiDateRangeToCustomDashFilter = (
  dateRange: TalentDiscoveryAiFilterSearchResponseV2['date_range'],
  systemMaxDate?: UTCDate | null
): DateRangeFilter | null => {
  if (dateRange === null || dateRange === undefined) {
    return null;
  }

  if (
    isRelativeDateRangePreset(dateRange.preset) &&
    systemMaxDate !== null &&
    systemMaxDate !== undefined
  ) {
    return {
      ...dateRangeDefinition,
      min: subMonths(
        systemMaxDate,
        RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK[dateRange.preset]
      ) as UTCDate,
      max: systemMaxDate,
      isUserSubmitted: true,
      isVisible: true,
    };
  }

  const min = parseAiDateToUtcDate(dateRange.start_value);
  const max = parseAiDateToUtcDate(dateRange.end_value);

  if (min === null && max === null) {
    return null;
  }

  return {
    ...dateRangeDefinition,
    min,
    max,
    isUserSubmitted: true,
    isVisible: true,
  };
};
